import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FilterBar } from '@/components/FilterBar';
import { Badge, Button, DataTable, EmptyState, PageHeader } from '@/components/ui';
import { api } from '@/lib/api';
export function DocumentsListPage() {
    const list = useQuery({
        queryKey: ['generated-documents'],
        queryFn: async () => (await api.get('/core/generated-documents/')).data,
    });
    const [filters, setFilters] = useState({ q: '', kind: '', state: '' });
    const search = (filters.q ?? '').toLowerCase().trim();
    const kindF = filters.kind ?? '';
    const stateF = filters.state ?? '';
    const filtered = useMemo(() => (list.data ?? []).filter((d) => {
        if (kindF && d.kind !== kindF)
            return false;
        if (stateF && d.state !== stateF)
            return false;
        if (!search)
            return true;
        return (d.title ?? '').toLowerCase().includes(search)
            || (d.reference ?? '').toLowerCase().includes(search)
            || (d.employee_name ?? '').toLowerCase().includes(search);
    }), [list.data, search, kindF, stateF]);
    return (_jsxs("div", { className: "space-y-4", children: [_jsx(PageHeader, { title: "Letters & documents", description: "Org-issued documents \u2014 employment letters, salary certificates, NOCs, custom letters.", actions: _jsx(Link, { to: "/documents/letters/new", children: _jsx(Button, { leftIcon: _jsx(Plus, { className: "h-4 w-4" }), children: "New document" }) }) }), _jsx(FilterBar, { filters: [
                    { key: 'q', kind: 'search', placeholder: 'Search title / reference / employee…' },
                    { key: 'kind', kind: 'select', label: 'Kind', options: [
                            { value: 'appointment', label: 'Appointment' },
                            { value: 'experience', label: 'Experience' },
                            { value: 'salary', label: 'Salary certificate' },
                            { value: 'increment', label: 'Increment' },
                            { value: 'termination', label: 'Termination' },
                            { value: 'resignation', label: 'Resignation' },
                            { value: 'noc', label: 'NOC' },
                        ] },
                    { key: 'state', kind: 'select', label: 'State', options: [
                            { value: 'draft', label: 'Draft' },
                            { value: 'issued', label: 'Issued' },
                            { value: 'sent', label: 'Sent' },
                            { value: 'archived', label: 'Archived' },
                        ] },
                ], value: filters, onChange: setFilters }), _jsx(DataTable, { rows: filtered, rowKey: (r) => r.id, loading: list.isLoading, empty: _jsx(EmptyState, { icon: _jsx(FileText, { className: "h-6 w-6" }), title: "No documents yet", description: "Issued letters and certificates land here. Generate one from an employee profile or click \u2018New document\u2019 above." }), columns: [
                    { key: 'title', header: 'Title', render: (d) => (_jsxs(Link, { to: `/documents/letters/${d.id}`, className: "flex items-center gap-2 font-medium hover:underline", children: [_jsx(FileText, { className: "h-3.5 w-3.5 text-muted-foreground" }), " ", d.title || '(untitled)'] })) },
                    { key: 'kind', header: 'Kind', render: (d) => _jsx(Badge, { tone: "info", children: d.kind.replaceAll('_', ' ') }) },
                    { key: 'emp', header: 'For', render: (d) => d.employee_name || '—' },
                    { key: 'ref', header: 'Ref', render: (d) => _jsx("code", { className: "font-mono text-xs", children: d.reference || '—' }) },
                    { key: 'state', header: 'State', render: (d) => (_jsx(Badge, { tone: d.state === 'issued' || d.state === 'sent' ? 'success' : d.state === 'archived' ? 'neutral' : 'warning', children: d.state })) },
                    { key: 'issued', header: 'Issued', render: (d) => d.issued_at ? new Date(d.issued_at).toLocaleDateString() : '—' },
                    { key: 'dl', header: '', align: 'right',
                        render: (d) => d.rendered_file_url
                            ? _jsxs("a", { href: d.rendered_file_url, target: "_blank", rel: "noreferrer", className: "inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline", children: [_jsx(Download, { className: "h-3 w-3" }), " open"] })
                            : null },
                ] })] }));
}
